<template>
  <b-modal
    id="inventory-form-modal"
    size="lg"
    hide-header
    no-close-on-esc
    no-close-on-backdrop
    hide-footer
  >
    <h4 class="mb-1 text-capitalize text-center py-1 title-user-wizard">
      {{ form.id ? $t("inventory.edit") : $t("inventory.new") }}
    </h4>

    <validation-observer ref="validationObserver" v-slot="{ handleSubmit }">
      <b-form @submit.prevent="handleSubmit(onSubmit)" @reset="onReset">
        <b-row class="m-2">
          <b-col cols="12" md="12">
            <validation-provider
              #default="validationContext"
              :name="$t('inventory.fields.unit')"
              rules="required"
            >
              <b-form-group
                id="id_unit_group"
                :label="$t('inventory.fields.unit')"
                label-for="id_unit"
              >
                <v-select
                  v-model="form.id_unit"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="unitsOptions"
                  :reduce="(val) => val.value"
                  input-id="id_unit"
                >
                </v-select>
                <b-form-invalid-feedback :state="getValidationState(validationContext)">
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>
          <b-col cols="12" md="6">
            <validation-provider
              #default="validationContext"
              :name="$t('inventory.fields.equipment_type')"
              rules="required"
            >
              <b-form-group
                id="id_equipment_type_group"
                :label="$t('inventory.fields.equipment_type')"
                label-for="id_equipment_type"
              >
                <v-select
                  v-model="form.id_inventory_equipment_type_catalog"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="equipmentOptions"
                  :reduce="(val) => val.value"
                  input-id="id_equipment_type"
                >
                </v-select>
                <b-form-invalid-feedback :state="getValidationState(validationContext)">
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>
          <b-col cols="12" md="6">
            <validation-provider
              #default="validationContext"
              :name="$t('inventory.fields.brand')"
              rules="required"
            >
              <b-form-group
                id="id_brand_group"
                :label="$t('inventory.fields.brand')"
                label-for="id_brand"
              >
                <v-select
                  v-model="form.id_inventory_brand_catalog"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="brandOptions"
                  :reduce="(val) => val.value"
                  input-id="id_brand"
                >
                </v-select>
                <b-form-invalid-feedback :state="getValidationState(validationContext)">
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>
          <b-col cols="12" md="6">
            <ValidationProvider
              :name="$t('inventory.fields.model')"
              rules="required"
              v-slot="{ errors }"
            >
              <b-form-group
                id="model_group"
                :label="$t('inventory.fields.model')"
                label-for="model"
              >
                <b-form-input
                  id="model"
                  v-model="form.model"
                  type="text"
                  placeholder=""
                />
                <span class="d-block invalid-feedback">{{ errors[0] }}</span>
              </b-form-group>
            </ValidationProvider>
          </b-col>
          <b-col cols="12" md="6">
            <ValidationProvider
              :name="$t('inventory.fields.serial_number')"
              rules="required"
              v-slot="{ errors }"
            >
              <b-form-group
                id="serial_number_group"
                :label="$t('inventory.fields.serial_number')"
                label-for="serial_number"
              >
                <b-form-input
                  id="serial_number"
                  v-model="form.serial_number"
                  type="text"
                  placeholder=""
                />
                <span class="d-block invalid-feedback">{{ errors[0] }}</span>
              </b-form-group>
            </ValidationProvider>
          </b-col>
          <b-col cols="12" md="6">
            <ValidationProvider
              :name="$t('inventory.fields.fixed_asset_number')"
              rules="required"
              v-slot="{ errors }"
            >
              <b-form-group
                id="fixed_asset_number_group"
                :label="$t('inventory.fields.fixed_asset_number')"
                label-for="fixed_asset_number"
              >
                <b-form-input
                  id="fixed_asset_number"
                  v-model="form.fixed_asset_number"
                  type="text"
                  placeholder=""
                />
                <span class="d-block invalid-feedback">{{ errors[0] }}</span>
              </b-form-group>
            </ValidationProvider>
          </b-col>

          <b-col cols="12" md="6">
            <validation-provider
              #default="validationContext"
              :name="$t('inventory.fields.installation_date')"
              rules="required"
            >
              <b-form-group
                id="installation_date_group"
                :label="$t('inventory.fields.installation_date')"
                label-for="installation_date"
              >
                <flat-pickr
                  v-model="form.installation_date"
                  class="form-control"
                  :name="form.installation_date"
                  placeholder=""
                />
                <b-form-invalid-feedback :state="getValidationState(validationContext)">
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col cols="12" md="6">
            <validation-provider
              #default="validationContext"
              :name="$t('inventory.fields.depreciation_start_date')"
              rules="required"
            >
              <b-form-group
                id="depreciation_start_date_group"
                :label="$t('inventory.fields.depreciation_start_date')"
                label-for="depreciation_start_date"
              >
                <flat-pickr
                  v-model="form.depreciation_start_date"
                  class="form-control"
                  :name="form.depreciation_start_date"
                  placeholder=""
                />
                <b-form-invalid-feedback :state="getValidationState(validationContext)">
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>
          <b-col cols="12" md="6">
            <ValidationProvider
              #default="validationContext"
              :name="$t('inventory.fields.equipment_cost')"
              rules="required"
            >
              <b-form-group
                id="equipment_cost_group"
                :label="$t('inventory.fields.equipment_cost')"
                label-for="equipment_cost"
              >
                <b-form-input
                  id="equipment_cost"
                  v-model="form.equipment_cost"
                  :state="getValidationState(validationContext)"
                  type="text"
                  trim
                />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </ValidationProvider>
          </b-col>
          <!--
              <b-col
                cols="12"
                md="6"
              >
                <ValidationProvider :name="$t('inventory.fields.accumulated_depreciation')" rules="required|numeric" v-slot="{ errors }">
                  <b-form-group
                      id="accumulated_depreciation_group"
                      :label="$t('inventory.fields.accumulated_depreciation')"
                      label-for="accumulated_depreciation"
                  >
                      <b-form-input
                        id="accumulated_depreciation"
                        v-model="form.accumulated_depreciation"
                        type="text"
                        placeholder=""
                      />
                      <span class="d-block invalid-feedback">{{ errors[0] }}</span>
                  </b-form-group>
                </ValidationProvider>
              </b-col>
              -->
          <b-col cols="12" md="6">
            <ValidationProvider
              :name="$t('inventory.fields.useful_life_months')"
              rules="required|numeric"
              v-slot="{ errors }"
            >
              <b-form-group
                id="useful_life_months_group"
                :label="$t('inventory.fields.useful_life_months')"
                label-for="useful_life_months"
              >
                <b-form-input
                  id="useful_life_months"
                  v-model="form.useful_life_months"
                  type="number"
                />
                <span class="d-block invalid-feedback">{{ errors[0] }}</span>
              </b-form-group>
            </ValidationProvider>
          </b-col>
          <!--
              <b-col
                cols="12"
                md="6"
              >
                <ValidationProvider :name="$t('inventory.fields.remaining_life_months')" rules="required|numeric" v-slot="{ errors }">
                  <b-form-group
                      id="remaining_life_months_group"
                      :label="$t('inventory.fields.remaining_life_months')"
                      label-for="remaining_life_months"
                  >
                      <b-form-input
                        id="remaining_life_months"
                        v-model="form.remaining_life_months"
                        type="text"
                        placeholder=""
                      />
                      <span class="d-block invalid-feedback">{{ errors[0] }}</span>
                  </b-form-group>
                </ValidationProvider>
              </b-col>
              -->
          <b-col cols="12" md="6">
            <ValidationProvider
              :name="$t('inventory.fields.status')"
              rules="required"
              v-slot="{ errors }"
            >
              <b-form-group
                id="status_group"
                :label="$t('inventory.fields.status')"
                label-for="status"
              >
                <b-form-select
                  v-model="form.status"
                  :options="statusOptions"
                ></b-form-select>
                <span class="d-block invalid-feedback">{{ errors[0] }}</span>
              </b-form-group>
            </ValidationProvider>
          </b-col>
          <b-col cols="12" md="6" v-if="isInstallationStatus">
            <validation-provider
              #default="validationContext"
              :name="$t('inventory.fields.project')"
              rules="required"
            >
              <b-form-group
                id="id_project_group"
                :label="$t('inventory.fields.project')"
                label-for="id_project"
              >
                <v-select
                  v-model="form.id_inventory_project_catalog"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="projectOptions"
                  :reduce="(val) => val.value"
                  input-id="id_project"
                >
                </v-select>
                <b-form-invalid-feedback :state="getValidationState(validationContext)">
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>
        </b-row>

        <b-row class="m-2">
          <b-col cols="12" md="12">
            <div class="d-flex">
              <div class="mr-auto">
                <button
                  type="button"
                  class="btn btn-danger text-capitalize"
                  @click="closeModal"
                  :disabled="isLoading"
                >
                  {{ $t("inventory.actions.cancel") }}
                </button>
              </div>
              <div class="d-flex justify-content-between">
                <div class="d-flex">
                  <button
                    type="submit"
                    class="btn btn-info text-capitalize"
                    :disabled="isLoading"
                  >
                    {{ $t("inventory.actions.save") }}
                  </button>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>
  </b-modal>
</template>

<script>
import { BRow, BCol } from "bootstrap-vue";
import { ref, computed, watch, getCurrentInstance } from "@vue/composition-api";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import store from "@/store";
import vSelect from "vue-select";
import axios from "@/libs/axios";

import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { useToast } from "vue-toastification/composition";

import SelectPaginateUnit from "../components/SelectPaginateUnit.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import flatPickr from "vue-flatpickr-component";

import inventoryStatus from "./constants/inventoryStatus";
import { getResponseErrors } from "@/utils/responseErrors";

function getDefaultForm() {
  return {
    id: "",
    id_unit: "",
    id_inventory_brand_catalog: "",
    id_inventory_project_catalog: "",
    id_inventory_equipment_type_catalog: "",
    model: "",
    serial_number: "",
    fixed_asset_number: "",
    installation_date: "",
    depreciation_start_date: "",
    equipment_cost: "",
    //accumulated_depreciation:'',
    useful_life_months: 60,
    //remaining_life_months:'',
    status: "",
  };
}

export default {
  name: "InventoryFormModal",
  components: {
    BRow,
    BCol,
    ValidationProvider,
    ValidationObserver,
    vSelect,
    SelectPaginateUnit,
    flatPickr,
  },
  props: {
    itemEdit: {
      type: Object,
      default: null,
    },
    brandOptions: {
      type: Array,
      default: [],
    },
    projectOptions: {
      type: Array,
      default: [],
    },
    equipmentOptions: {
      type: Array,
      default: [],
    },
    unitsOptions: {
      type: Array,
      default: [],
    },
    statusOptions: {
      type: Array,
      default: [],
    },
  },
  setup(props, { emit, root }) {
    const vm = getCurrentInstance().proxy;

    const toast = useToast();

    const form = ref(getDefaultForm());
    const isLoading = ref(false);
    const validationObserver = ref(null);

    const { refFormObserver, getValidationState } = formValidation();

    async function onSubmit() {
      isLoading.value = true;

      try {
        const { id } = form.value;
        let response = null;

        if (id) {
          response = await axios.put(`/v1/inventories/${id}`, form.value);
        } else {
          response = await axios.post("/v1/inventories", form.value);
        }

        toast({
          component: ToastificationContent,
          props: {
            title: response.data.message,
            icon: "CheckIcon",
            variant: "success",
          },
        });

        emit("form-saved");

        setTimeout(() => onReset(), 300);

      } catch (response) {
        const { message, strValidationErrors } = getResponseErrors(response);

        toast(
          {
            component: ToastificationContent,
            props: {
              title: strValidationErrors ? strValidationErrors : message,
              icon: "AlertTriangleIcon",
              variant: "danger",
              text: message,
            },
          },
          {
            timeout: 10000,
          }
        );
      } finally {
        isLoading.value = false;
      }
    }

    function onReset() {
      form.value = getDefaultForm();
    }

    function closeModal() {
      root.$emit("bv::hide::modal", "inventory-form-modal");
    }

    watch(
      () => props.itemEdit,
      (newVal) => {
        if (newVal) {
          form.value = { ...getDefaultForm(), ...newVal };
        } else {
          form.value = getDefaultForm();
        }
      },
      { immediate: true }
    );

    const isInstallationStatus = computed(() => {
      return form.value.status === inventoryStatus.INSTALLED;
    });

    return {
      form,
      isLoading,
      onReset,
      onSubmit,
      validationObserver,
      closeModal,
      refFormObserver,
      getValidationState,
      isInstallationStatus,
    };
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

.errorText {
  color: red;
  font-size: 12px;
}
</style>

